import { IssueFragment, IssueLevel, UnitType } from "generated-graphql/graphql";

import {
  canOverrideReportingUnit,
  describeReportingUnitOverrideRefusal,
} from "../../ReportingFacilityChemical/reportingUnitOverrideEligibility";

type ReportingUnitOverrideRfc = Parameters<typeof canOverrideReportingUnit>[0];

type ReportingUnitOverrideFormFields = {
  reportingUnitOverrideEnabled?: boolean | null;
  reportingUnitOverride?: UnitType | null;
  reportingUnitOverrideReason?: string | null;
};

/**
 * Client-side validation issues for the staff reporting-unit override: the
 * reason, required as soon as a unit is selected, and whether that unit can
 * actually be honoured.
 *
 * Goes through the form's validator rather than a Controller `rules` because
 * `useValidatingForm` installs a resolver, and react-hook-form ignores
 * built-in field rules whenever one is present. Same shape as
 * `getExemptValidationIssues`.
 *
 * Two guards are load-bearing:
 *
 *  - `reportingUnitOverrideEnabled`, so collapsing the panel does not leave a
 *    required-reason error on a field that is no longer visible, blocking
 *    every save with nothing on screen to explain why.
 *  - `isStaff`, because `reportingUnitOverrideEnabled` seeds from the override
 *    row for every user. Without it, a staff-set override would block a
 *    customer's save the moment it became unhonourable, on a panel they
 *    cannot see and cannot clear.
 *
 * The unit check runs the same `canOverrideReportingUnit` predicate that
 * disables options in the select and that the resolver refuses on. Disabling
 * handles selection time; this catches a unit that was valid when saved and is
 * not anymore.
 */
export function getReportingUnitOverrideValidationIssues(
  data: ReportingUnitOverrideFormFields,
  modelName: string,
  rfc: ReportingUnitOverrideRfc | null | undefined,
  isStaff: boolean
): IssueFragment[] {
  if (!isStaff) {
    return [];
  }
  if (!data.reportingUnitOverrideEnabled || !data.reportingUnitOverride) {
    return [];
  }
  if (!data.reportingUnitOverrideReason?.trim()) {
    return [
      {
        __typename: "Issue",
        level: IssueLevel.Error,
        message: "A reason is required when the reporting unit is overridden.",
        key: "reportingUnitOverrideReason",
        modelName,
        modelId: null,
      },
    ];
  }

  const result = canOverrideReportingUnit(rfc, data.reportingUnitOverride);
  if (!result.ok) {
    return [
      {
        __typename: "Issue",
        level: IssueLevel.Error,
        message: describeReportingUnitOverrideRefusal(data.reportingUnitOverride, result),
        key: "reportingUnitOverride",
        modelName,
        modelId: null,
      },
    ];
  }

  return [];
}
